/* Local interaction runtime for the supplied reference markup. Made by ibrahim hmayel. */
'use strict';
const ON_CHANGE_DEBOUNCE_TIMER = 300;
const PUB_SUB_EVENTS = Object.freeze({
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  variantChange: 'variant-change',
  cartError: 'cart-error',
  optionValueSelectionChange: 'option-value-selection-change',
});
const ihSubscribers = new Map();
function subscribe(topic, callback) {
  if (!ihSubscribers.has(topic)) ihSubscribers.set(topic, new Set());
  ihSubscribers.get(topic).add(callback);
  return () => ihSubscribers.get(topic)?.delete(callback);
}
function publish(topic, data) {
  ihSubscribers.get(topic)?.forEach((fn) => {
    try {
      fn(data);
    } catch (error) {
      console.warn('Reference event', error);
    }
  });
}
function debounce(fn, delay = 300) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}
function throttle(fn, delay = 100) {
  let last = 0;
  return function (...args) {
    if (Date.now() - last >= delay) {
      last = Date.now();
      fn.apply(this, args);
    }
  };
}
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: type === 'javascript' ? 'application/javascript' : 'application/json',
    },
  };
}
function getFocusableElements(container) {
  return container
    ? Array.from(
        container.querySelectorAll(
          'a[href],button:not([disabled]),input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])',
        ),
      ).filter((node) => !node.closest('[hidden],[inert]') && node.getClientRects().length)
    : [];
}
let ihFocusTrap;
function removeTrapFocus(focus) {
  if (ihFocusTrap) document.removeEventListener('keydown', ihFocusTrap);
  ihFocusTrap = null;
  if (focus?.isConnected) focus.focus({ preventScroll: true });
}
function trapFocus(container, focus = container) {
  removeTrapFocus();
  if (!container) return;
  ihFocusTrap = (event) => {
    if (event.key !== 'Tab') return;
    const nodes = getFocusableElements(container),
      first = nodes[0],
      last = nodes[nodes.length - 1];
    if (!first) {
      event.preventDefault();
      container.focus();
      return;
    }
    if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      (document.activeElement === last || !container.contains(document.activeElement))
    ) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', ihFocusTrap);
  (focus || getFocusableElements(container)[0])?.focus({ preventScroll: true });
}
function onKeyUpEscape(event) {
  if (event.code?.toUpperCase() !== 'ESCAPE') return;
  const details = event.target.closest('details[open]');
  if (details) {
    details.open = false;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    details.querySelector('summary')?.focus();
  }
}
function pauseAllMedia() {
  document.querySelectorAll('video').forEach((video) => video.pause());
  document
    .querySelectorAll('iframe.js-youtube')
    .forEach((frame) =>
      frame.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        'https://www.youtube.com',
      ),
    );
  document
    .querySelectorAll('iframe.js-vimeo')
    .forEach((frame) =>
      frame.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), 'https://player.vimeo.com'),
    );
  document.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
}
window.pauseAllMedia = pauseAllMedia;
const IHReference = (() => {
  try {
    return JSON.parse(document.getElementById('ih-reference-config')?.textContent || '{}');
  } catch {
    return {};
  }
})();
function ihRoot() {
  return IHReference.root || window.Shopify?.routes?.root || '/';
}
function ihRoutes() {
  return {
    cart_add_url: ihRoot() + 'cart/add',
    cart_change_url: ihRoot() + 'cart/change',
    cart_update_url: ihRoot() + 'cart/update',
    cart_url: ihRoot() + 'cart',
    ...window.routes,
  };
}
function ihMoney(value, format = IHReference.moneyFormat || '{{amount}}') {
  const number = (Number(value) || 0) / 100;
  const digits = ['JOD', 'KWD', 'BHD', 'OMR', 'TND'].includes(IHReference.currency) ? 3 : 2;
  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    const precision = key.includes('no_decimals') ? 0 : digits;
    const comma = key.includes('comma_separator');
    let [whole, decimal] = number.toFixed(precision).split('.');
    const separator = key.includes('apostrophe') ? "'" : key.includes('space') ? ' ' : comma ? '.' : ',';
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    return whole + (decimal === undefined ? '' : (comma ? ',' : '.') + decimal);
  });
}
async function ihJSON(url, options = {}) {
  const response = await fetch(url, { credentials: 'same-origin', ...options });
  let body;
  try {
    body = await response.json();
  } catch {
    throw new Error(window.cartStrings?.error || 'Unable to complete this request.');
  }
  if (!response.ok || body.status >= 400)
    throw new Error(
      body.description || body.message || window.cartStrings?.error || 'Unable to complete this request.',
    );
  return body;
}
function ihDefine(name, definition) {
  if (!customElements.get(name)) customElements.define(name, definition);
}
function ihParse(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

window.Shopify = window.Shopify || {};
Shopify.bind = Shopify.bind || ((fn, scope) => fn.bind(scope));
Shopify.setSelectorByValue =
  Shopify.setSelectorByValue ||
  ((select, value) => {
    for (let i = 0; i < select.options.length; i++)
      if (select.options[i].value === value || select.options[i].text === value) {
        select.selectedIndex = i;
        return i;
      }
    return -1;
  });
Shopify.postLink =
  Shopify.postLink ||
  ((path, options = {}) => {
    const form = document.createElement('form');
    form.method = options.method || 'post';
    form.action = path;
    Object.entries(options.parameters || {}).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.append(input);
    });
    document.body.append(form);
    form.submit();
  });
Shopify.CountryProvinceSelector =
  Shopify.CountryProvinceSelector ||
  class {
    constructor(countryId, provinceId, options = {}) {
      this.country = document.getElementById(countryId);
      this.province = document.getElementById(provinceId);
      this.container = document.getElementById(options.hideElement || provinceId);
      if (!this.country || !this.province) return;
      Shopify.setSelectorByValue(this.country, this.country.dataset.default || '');
      this.country.addEventListener('change', () => this.update());
      this.update();
      Shopify.setSelectorByValue(this.province, this.province.dataset.default || '');
    }
    update() {
      const selected = this.country.selectedOptions[0];
      let values = [];
      try {
        values = JSON.parse(selected?.dataset.provinces || '[]');
      } catch {}
      this.province.replaceChildren(
        ...values.map(([value, label]) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = label;
          return option;
        }),
      );
      if (this.container) this.container.style.display = values.length ? '' : 'none';
    }
  };

class QuantityInput extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', (event) => {
        event.preventDefault();
        const old = this.input.value;
        const down = button.name === 'minus' || button.dataset.action === 'minus';
        try {
          down ? this.input.stepDown() : this.input.stepUp();
        } catch {}
        if (old !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        this.validateQtyRules();
      }),
    );
    this.input.addEventListener('change', () => this.validateQtyRules());
    this._unsub = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.validateQtyRules());
    this.validateQtyRules();
  }
  validateQtyRules() {
    if (!this.input) return;
    const n = Number(this.input.value),
      min = Number(this.input.min || 0),
      max = this.input.max === '' ? Infinity : Number(this.input.max);
    this.querySelector('button[name="minus"]')?.classList.toggle('disabled', n <= min);
    this.querySelector('button[name="plus"]')?.classList.toggle('disabled', n >= max);
  }
  disconnectedCallback() {
    this._unsub?.();
  }
}
ihDefine('quantity-input', QuantityInput);

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
  }
  connectedCallback() {
    if (this._searchReady) return;
    this._searchReady = true;
    this.input?.addEventListener(
      'input',
      debounce(() => this.onChange(), 250),
    );
    this.querySelector('form')?.addEventListener('reset', (event) => this.onFormReset(event));
  }
  onChange() {
    this.toggleResetButton();
  }
  toggleResetButton() {
    this.resetButton?.classList.toggle('hidden', !this.input?.value);
  }
  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }
  onFormReset(event) {
    event?.preventDefault();
    if (this.shouldResetForm()) {
      if (this.input) this.input.value = '';
      this.toggleResetButton();
      this.input?.focus();
    }
  }
}
ihDefine('search-form', SearchForm);
class PredictiveSearch extends SearchForm {
  connectedCallback() {
    super.connectedCallback();
    if (this._predictiveReady) return;
    this._predictiveReady = true;
    this.results = this.querySelector('[data-predictive-search]');
    this.querySelector('form')?.addEventListener('submit', (event) => {
      if (!this.input?.value.trim()) event.preventDefault();
    });
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('focusout', () =>
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0),
    );
    this.input?.addEventListener('focus', () => {
      if (this.input.value.trim().length > 1) this.onChange();
    });
  }
  onChange() {
    super.onChange();
    this.getSearchResults(this.input.value.trim());
  }
  async getSearchResults(query) {
    this._abort?.abort();
    if (!query) {
      this.close();
      return;
    }
    this._abort = new AbortController();
    this.setAttribute('loading', '');
    try {
      const url = new URL(ihRoot() + 'search/suggest', location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[limit]', '8');
      const response = await fetch(url.pathname + url.search, {
        signal: this._abort.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error();
      const doc = ihParse(await response.text());
      const content = doc.querySelector('#shopify-section-predictive-search');
      if (content && this.results) {
        this.results.innerHTML = content.innerHTML;
        this.open();
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.close();
    } finally {
      this.removeAttribute('loading');
    }
  }
  open() {
    this.setAttribute('open', 'true');
    this.setAttribute('results', 'true');
    this.input?.setAttribute('aria-expanded', 'true');
    this.querySelector('[role="status"]')?.setAttribute('aria-hidden', 'false');
  }
  close() {
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
  }
  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) return;
    const items = Array.from(this.querySelectorAll('[role="option"]'));
    if (event.key === 'Enter') {
      const selected = items.find((item) => item.getAttribute('aria-selected') === 'true');
      const link = selected?.querySelector('a,button');
      if (link) {
        event.preventDefault();
        link.click();
      }
      return;
    }
    if (!items.length) return;
    event.preventDefault();
    let index = items.findIndex((item) => item.getAttribute('aria-selected') === 'true');
    index = (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items.forEach((item, i) => item.setAttribute('aria-selected', String(i === index)));
    if (!items[index].id) items[index].id = 'ih-search-option-' + index;
    this.input?.setAttribute('aria-activedescendant', items[index].id);
  }
  onFormReset(event) {
    super.onFormReset(event);
    this.close();
  }
  disconnectedCallback() {
    this._abort?.abort();
  }
}
ihDefine('predictive-search', PredictiveSearch);

class CartDrawer extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.addEventListener('click', (event) => {
      if (event.target.closest('.drawer__close,#CartDrawer-Overlay')) this.close();
    });
    this.addEventListener('keyup', (event) => {
      if (event.key === 'Escape') this.close();
    });
    this.installCartLink();
  }
  installCartLink() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link || link._ihCart) return;
    link._ihCart = true;
    link.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(link);
    });
  }
  open(opener) {
    this.setActiveElement(opener || document.activeElement);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    const content = this.querySelector('#CartDrawer') || this;
    setTimeout(() => trapFocus(content, this.querySelector('.drawer__close') || content), 30);
    this.querySelector('countdown-timer')?.playTimer?.();
  }
  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.activeElement);
  }
  setActiveElement(element) {
    this.activeElement = element;
  }
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {
    return ihParse(html || '').querySelector(selector)?.innerHTML || '';
  }
  renderContents(state, dontOpen = false) {
    const html = state.sections?.['cart-drawer'];
    if (html) {
      const fresh = ihParse(html).querySelector('cart-drawer');
      if (fresh) this.innerHTML = fresh.innerHTML;
      this.classList.toggle('is-empty', !(state.item_count ?? this.querySelector('.cart-item')));
    }
    const icon = state.sections?.['cart-icon-bubble'];
    if (icon && document.getElementById('cart-icon-bubble')) {
      const doc = ihParse(icon);
      document.getElementById('cart-icon-bubble').innerHTML = (
        doc.querySelector('.shopify-section') || doc.body
      ).innerHTML;
    }
    this.installCartLink();
    if (!dontOpen) this.open();
  }
}
ihDefine('cart-drawer', CartDrawer);
let ihCartQueue = Promise.resolve();
function ihCartSections() {
  const ids = ['cart-drawer', 'cart-icon-bubble'];
  for (const key of ['main-cart-items', 'main-cart-footer']) {
    const node = document.getElementById(key);
    if (node?.dataset.id) ids.push(node.dataset.id);
  }
  return [...new Set(ids)].slice(0, 5);
}
function ihApplyCart(state, source = 'ih-reference') {
  const drawer = document.querySelector('cart-drawer');
  drawer?.renderContents(state, true);
  for (const id of ['main-cart-items', 'main-cart-footer']) {
    const node = document.getElementById(id);
    if (!node) continue;
    const html = state.sections?.[node.dataset.id];
    if (!html) continue;
    const doc = ihParse(html);
    const fresh = doc.getElementById(id);
    if (fresh) {
      const oldContents = node.querySelector('.js-contents'),
        newContents = fresh.querySelector('.js-contents');
      if (oldContents && newContents) oldContents.innerHTML = newContents.innerHTML;
      else node.innerHTML = fresh.innerHTML;
    }
    node.classList.toggle('is-empty', state.item_count === 0);
  }
  document.querySelector('.cart__footer')?.classList.toggle('is-empty', state.item_count === 0);
  publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: state });
}
async function ihChangeCart(line, quantity) {
  const task = async () => {
    const body = { quantity: Number(quantity), sections: ihCartSections(), sections_url: location.pathname };
    if (/^\d+$/.test(String(line))) body.line = Number(line);
    else body.id = line;
    const state = await ihJSON(ihRoutes().cart_change_url + '.js', {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    ihApplyCart(state);
    return state;
  };
  const result = ihCartQueue.then(task, task);
  ihCartQueue = result.catch(() => {});
  return result;
}
class CartItems extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.addEventListener(
      'change',
      debounce((event) => {
        if (event.target.matches('input[name="updates[]"],.quantity__input')) this.onChange(event);
      }, ON_CHANGE_DEBOUNCE_TIMER),
    );
    this._unsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'product-form') this.onCartUpdate();
    });
  }
  onChange(event) {
    const input = event.target;
    this.updateQuantity(
      input.dataset.index || input.closest('[data-index]')?.dataset.index,
      input.value,
      document.activeElement?.name,
      input.dataset.quantityVariantId,
    );
  }
  async updateQuantity(line, quantity, name, variantId) {
    if (!line) return;
    this.classList.add('cart__items--disabled');
    try {
      await ihChangeCart(line, quantity);
    } catch (error) {
      const node =
        this.querySelector('.cart-item__error-text,#cart-errors,#CartDrawer-CartErrors') ||
        document.getElementById('cart-errors');
      if (node) node.textContent = error.message;
      publish(PUB_SUB_EVENTS.cartError, { message: error.message, variantId });
    } finally {
      this.classList.remove('cart__items--disabled');
    }
  }
  async onCartUpdate() {
    try {
      const ids = ihCartSections(),
        url = new URL(ihRoutes().cart_url, location.origin);
      url.searchParams.set('sections', ids.join(','));
      const sections = await ihJSON(url.pathname + url.search);
      ihApplyCart({ sections }, 'cart-items-refresh');
    } catch {}
  }
  getSectionsToRender() {
    return ihCartSections().map((id) => ({ id, section: id, selector: '.js-contents' }));
  }
  enableLoading() {
    this.classList.add('cart__items--disabled');
  }
  disableLoading() {
    this.classList.remove('cart__items--disabled');
  }
  disconnectedCallback() {
    this._unsub?.();
  }
}
ihDefine('cart-items', CartItems);
ihDefine('cart-drawer-items', class extends CartItems {});
ihDefine(
  'cart-remove-button',
  class extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.addEventListener('click', (event) => {
        event.preventDefault();
        const items = this.closest('cart-items,cart-drawer-items');
        if (items) items.updateQuantity(this.dataset.index, 0);
        else ihChangeCart(this.dataset.index, 0).catch(console.error);
      });
    }
  },
);
ihDefine(
  'cart-note',
  class extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.addEventListener(
        'input',
        debounce((event) => {
          ihJSON(ihRoutes().cart_update_url + '.js', {
            ...fetchConfig(),
            body: JSON.stringify({ note: event.target.value }),
          }).catch(() => {});
        }, 400),
      );
    }
  },
);

class ProductForm extends HTMLElement {
  handleSubmit(event) {
    event.preventDefault();
    this._skipCart = event.currentTarget?.dataset.skipCartButton === 'true';
    this.form?.requestSubmit();
  }
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.addEventListener('submit', (event) => {
      if (event.defaultPrevented) return;
      event.preventDefault();
      this.onSubmitHandler(event);
    });
  }
  async onSubmitHandler(event) {
    if (this._pending) return;
    const form = this.form;
    if (!form) return;
    const fields = Array.from(
      document.querySelectorAll(`custom-product-field[data-section="${this.dataset.section}"]`),
    );
    if (fields.some((field) => !field.validate?.())) {
      this.handleErrorMessage(
        fields.find((field) => !field.validate?.())?.dataset.atcErrorMsg || 'Complete the required fields.',
      );
      return;
    }
    if (!form.reportValidity()) return;
    this._pending = true;
    this.submitButton = this.querySelector('[type="submit"]');
    const oldDisabled = this.submitButton?.disabled;
    this.submitButton?.setAttribute('aria-disabled', 'true');
    this.submitButton?.classList.add('loading');
    this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');
    this.handleErrorMessage();
    let added = false;
    try {
      const data = new FormData(form);
      fields.forEach((field) => data.set('properties[' + field.dataset.name + ']', field.getValue()));
      const localSection = this.closest('.shopify-section') || this.closest('section') || this;
      const bundle = this.closest('bundle-deals') || localSection.querySelector('main-bundle-offer');
      const breaks = localSection.querySelector('quantity-breaks');
      let items = bundle?.getItems() ||
        breaks?.getItems() || [{ id: data.get('id'), quantity: Number(data.get('quantity') || 1) }];
      if (!bundle) {
        localSection
          .querySelectorAll('product-info-upsell[data-selected="true"]:not([data-main-offer-item="true"])')
          .forEach((upsell) => items.push({ id: upsell.dataset.id, quantity: 1 }));
        localSection.querySelectorAll('quantity-gifts').forEach((gifts) => items.push(...gifts.getItems()));
      }
      if (bundle || breaks || items.length > 1) {
        const properties = Array.from(data.entries()).filter(([key]) => key.startsWith('properties['));
        const sellingPlan = data.get('selling_plan');
        data.delete('id');
        data.delete('quantity');
        properties.forEach(([key]) => data.delete(key));
        data.delete('selling_plan');
        items = ihCombineItems(items);
        if (!items.length) throw new Error('Select at least one item.');
        items.forEach((item, index) => {
          data.set('items[' + index + '][id]', item.id);
          data.set('items[' + index + '][quantity]', String(item.quantity));
          if (index === 0) {
            properties.forEach(([key, value]) =>
              data.set('items[' + index + '][properties][' + key.slice(11, -1) + ']', value),
            );
            if (sellingPlan) data.set('items[' + index + '][selling_plan]', sellingPlan);
          }
        });
      }
      const cart = document.querySelector('cart-drawer,cart-notification');
      const sections = cart?.getSectionsToRender?.().map((item) => item.section || item.id) || [
        'cart-icon-bubble',
      ];
      data.set('sections', sections.join(','));
      data.set('sections_url', location.pathname);
      const state = await ihJSON(ihRoutes().cart_add_url + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: data,
      });
      added = true;
      const skip = form.querySelector('[name="id"]')?.dataset.skipCart === 'true' || this._skipCart;
      this._skipCart = false;
      if (skip) {
        location.assign(ihRoot() + 'checkout');
        return;
      }
      if (cart) {
        if (!state.sections || sections.some((id) => !state.sections[id])) {
          location.assign(ihRoutes().cart_url);
          return;
        }
        cart.renderContents(state);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: data.get('id'),
          cartData: state,
        });
      } else location.assign(ihRoutes().cart_url);
    } catch (error) {
      if (added) location.assign(ihRoutes().cart_url);
      else {
        this.handleErrorMessage(error.message);
        publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', message: error.message });
      }
    } finally {
      this._pending = false;
      this.submitButton?.removeAttribute('aria-disabled');
      this.submitButton?.classList.remove('loading');
      if (this.submitButton) this.submitButton.disabled = oldDisabled;
      this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
    }
  }
  handleErrorMessage(message = '') {
    const wrapper = this.querySelector('.product-form__error-message-wrapper');
    if (wrapper) wrapper.hidden = !message;
    const text = this.querySelector('.product-form__error-message');
    if (text) text.textContent = message;
  }
}
ihDefine('product-form', ProductForm);
class ProductInfo extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.querySelectorAll('input[name="quantity"]').forEach((input) =>
      input.addEventListener('change', () => publish(PUB_SUB_EVENTS.quantityUpdate, { source: this.id })),
    );
  }
  initShareLinks() {}
  updateQuantityRules() {
    publish(PUB_SUB_EVENTS.quantityUpdate, { source: this.id });
  }
}
ihDefine('product-info', ProductInfo);

class VariantSelects extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    try {
      this.variantData = JSON.parse(
        this.querySelector('script[type="application/json"]')?.textContent || '[]',
      );
    } catch {
      this.variantData = [];
    }
    this.addEventListener('change', () => this.onVariantChange());
    this.updateOptionStatuses();
  }
  getVariantData() {
    return this.variantData;
  }
  updateOptions() {
    this.options = Array.from(this.querySelectorAll('.product-form__input'))
      .filter((node) => node.querySelector('select,input[type="radio"]'))
      .map(
        (node) =>
          node.querySelector('select')?.value || node.querySelector('input[type="radio"]:checked')?.value,
      );
  }
  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) =>
      (variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)).every(
        (value, index) => value === this.options[index],
      ),
    );
  }
  async onVariantChange() {
    this.updateOptions();
    this.updateMasterId();
    this.updateOptionStatuses();
    if (!this.currentVariant) {
      this.toggleAddButton(true, window.variantStrings?.unavailable || 'Unavailable');
      return;
    }
    const variant = this.currentVariant;
    this.updateVariantInput();
    this.updateURL();
    this.updateMedia();
    this.toggleAddButton(
      !variant.available,
      variant.available ? window.variantStrings?.addToCart : window.variantStrings?.soldOut,
    );
    this.removeAttribute('data-unavailable');
    this._abort?.abort();
    this._abort = new AbortController();
    const sectionId = this.dataset.originalSection || this.dataset.section;
    try {
      const url = new URL(this.dataset.url, location.origin);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', sectionId);
      const response = await fetch(url.pathname + url.search, {
        signal: this._abort.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) return;
      const doc = ihParse(await response.text());
      for (const prefix of [
        'price',
        'Sku',
        'Inventory',
        'Volume',
        'Price-Per-Item',
        'Quantity-Rules',
        'Quantity-Form',
        'main-atc-price',
      ]) {
        const fresh = doc.getElementById(prefix + '-' + sectionId),
          current = document.getElementById(prefix + '-' + this.dataset.section);
        if (fresh && current) {
          current.innerHTML = fresh.innerHTML;
          current.className = fresh.className;
        }
      }
      this.renderProductInfo(doc);
      publish(PUB_SUB_EVENTS.variantChange, {
        data: { sectionId: this.dataset.section, html: doc, variant },
        variant,
      });
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Variant update unavailable', error);
    }
  }
  updateVariantInput() {
    const id = this.dataset.section;
    document
      .querySelectorAll(`form[id="product-form-${id}"], form[id="product-form-installment-${id}"]`)
      .forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if (input) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    this.closest('section')
      ?.querySelectorAll('pickup-availability')
      .forEach((pickup) => pickup.fetchAvailability?.(this.currentVariant.id));
  }
  updateURL() {
    if (this.dataset.updateUrl === 'false') return;
    const url = new URL(this.dataset.url, location.origin);
    url.searchParams.set('variant', this.currentVariant.id);
    history.replaceState({}, '', url.pathname + url.search);
  }
  updateMedia() {
    if (!this.currentVariant.featured_media) return;
    document.querySelectorAll(`media-gallery[id*="${this.dataset.section}"]`).forEach((gallery) => {
      try {
        gallery.setActiveMedia?.(
          this.dataset.section + '-' + this.currentVariant.featured_media.id,
          true,
          this.dataset.hasFiltering === 'true',
          this.currentVariant,
        );
      } catch {}
    });
  }
  toggleAddButton(disable, text) {
    document
      .querySelectorAll(
        `#ProductSubmitButton-${CSS.escape(this.dataset.section)},#SectionAtcBtn-${CSS.escape(this.dataset.section)}`,
      )
      .forEach((button) => {
        button.disabled = disable;
        const label =
          button.querySelector('.main-atc__label__text,.sticky-atc__label') ||
          button.querySelector('.button__label');
        if (label && text) label.textContent = text;
      });
  }
  removeErrorMessage() {
    this.closest('section')?.querySelector('product-form')?.handleErrorMessage();
  }
  renderProductInfo(doc) {
    if (this.currentVariant)
      this.closest('section')
        ?.querySelectorAll('.dynamic-price[data-text]')
        .forEach((node) => {
          if (!node.closest('quantity-breaks'))
            node.innerHTML = node.dataset.text.replace(/\[price\]/g, ihMoney(this.currentVariant.price));
        });
  }
  updateOptionStatuses() {
    const groups = Array.from(this.querySelectorAll('.product-form__input')).filter((node) =>
      node.querySelector('select,input[type="radio"]'),
    );
    const selected = groups.map(
      (group) => group.querySelector('select')?.value || group.querySelector('input:checked')?.value,
    );
    groups.forEach((group, index) => {
      group.querySelectorAll('option,input[type="radio"]').forEach((input) => {
        const available = this.variantData.some(
          (variant) =>
            variant.available &&
            (variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)).every(
              (value, i) => (i === index ? value === input.value : i < index ? value === selected[i] : true),
            ),
        );
        input.classList.toggle('disabled', !available);
        input.dataset.available = String(available);
      });
    });
  }
  disconnectedCallback() {
    this._abort?.abort();
  }
}
ihDefine('variant-selects', VariantSelects);

document.addEventListener('click', (event) => {
  const button = event.target.closest('.main-product-atc[type="button"], [data-skip-cart-button]');
  if (!button || button.disabled || event.defaultPrevented) return;
  const section = button.closest('section,.shopify-section');
  const productForm =
    section?.querySelector('product-form.main-product-form') ||
    document.querySelector('product-form.main-product-form');
  if (productForm?.form) {
    event.preventDefault();
    productForm._skipCart = button.dataset.skipCartButton === 'true';
    productForm.form.requestSubmit();
  }
});

function ihVariants(node) {
  try {
    return JSON.parse(node.querySelector('script[type="application/json"]')?.textContent || '[]');
  } catch {
    return [];
  }
}
function ihSelectedVariant(node) {
  const values = Array.from(node.querySelectorAll('select')).map((select) => select.value);
  return ihVariants(node).find((variant) =>
    (variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean)).every(
      (option, index) => option === values[index],
    ),
  );
}
function ihCombineItems(items) {
  const combined = new Map();
  items
    .filter((item) => item.id && item.quantity > 0)
    .forEach((item) => {
      const key = String(item.id) + JSON.stringify(item.properties || {}) + String(item.selling_plan || '');
      if (combined.has(key)) combined.get(key).quantity += item.quantity;
      else combined.set(key, { ...item });
    });
  return Array.from(combined.values());
}
class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.addEventListener('change', () => this.update());
    this.update();
  }
  getSelected() {
    const input = this.querySelector('input[type="radio"]:checked');
    return (
      input?.closest('label') ||
      Array.from(this.querySelectorAll('.quantity-break')).find(
        (label) => label.dataset.input === input?.dataset.input,
      )
    );
  }
  getItems() {
    const label = this.getSelected(),
      quantity = Number(
        this.querySelector('input[type="radio"]:checked')?.value || label?.dataset.quantity || 1,
      );
    const variants = ihVariants(this),
      rows = Array.from(label?.querySelectorAll('.quantity-break__selector-item') || []);
    const defaultId = document
      .getElementById('product-form-' + this.dataset.section)
      ?.querySelector('[name="id"]')?.value;
    if (rows.length) {
      return ihCombineItems(
        rows.map((row) => {
          const options = Array.from(row.querySelectorAll('select')).map((select) => select.value);
          const variant = variants.find((v) =>
            (v.options || []).every((value, index) => value === options[index]),
          );
          return { id: variant?.id || defaultId, quantity: 1 };
        }),
      );
    }
    return [{ id: defaultId, quantity }];
  }
  update() {
    const label = this.getSelected();
    this.querySelectorAll('.quantity-break').forEach((node) => {
      const selected = node === label;
      node.classList.toggle('quantity-break--selected', selected);
      node.dataset.selected = String(selected);
    });
    const count = Number(this.querySelector('input:checked')?.value || label?.dataset.quantity || 1);
    document
      .querySelectorAll(`quantity-gifts[data-section="${this.dataset.section}"]`)
      .forEach((gifts) => gifts.update?.(count));
    this.querySelectorAll('.quantity-break').forEach((node) => {
      const n = Number(node.dataset.quantity || 1),
        data = ihVariants(this),
        rows = Array.from(node.querySelectorAll('.quantity-break__selector-item'));
      let subtotal = 0,
        compare = 0;
      if (rows.length) {
        rows.forEach((row) => {
          const options = Array.from(row.querySelectorAll('select')).map((select) => select.value),
            variant =
              data.find((v) => (v.options || []).every((value, index) => value === options[index])) ||
              data[0];
          subtotal += Number(variant?.price || 0);
          compare += Number(variant?.compare_at_price || variant?.price || 0);
        });
      } else {
        const id = document
            .getElementById('product-form-' + this.dataset.section)
            ?.querySelector('[name="id"]')?.value,
          variant = data.find((v) => String(v.id) === id) || data[0];
        subtotal = n * Number(variant?.price || 0);
        compare = n * Number(variant?.compare_at_price || variant?.price || 0);
      }
      if (this.dataset.updatePrices !== 'true') return;
      const price = Math.max(
        0,
        subtotal * Number(node.dataset.percentageLeft || 1) - Number(node.dataset.fixedDiscount || 0),
      );
      const saved = compare - price;
      const tokens = {
        price: ihMoney(price, this.dataset.moneyFormat),
        compare_price: ihMoney(compare, this.dataset.moneyFormat),
        amount_saved: ihMoney(saved, this.dataset.moneyFormat),
        amount_saved_rounded: ihMoney(Math.round(saved / 100) * 100, this.dataset.moneyFormat),
        price_each: ihMoney(price / n, this.dataset.moneyFormat),
        compare_price_each: ihMoney(compare / n, this.dataset.moneyFormat),
      };
      node.querySelectorAll('[data-text]').forEach((text) => {
        text.innerHTML = text.dataset.text.replace(/\[([a-z_]+)\]/g, (all, key) => tokens[key] ?? all);
      });
    });
    publish(PUB_SUB_EVENTS.quantityUpdate, { quantity: count, source: 'quantity-breaks' });
  }
}
ihDefine('quantity-breaks', QuantityBreaks);
class QuantityGifts extends HTMLElement {
  connectedCallback() {
    this.update(
      Number(
        document.querySelector(`quantity-breaks[data-section="${this.dataset.section}"] input:checked`)
          ?.value || 1,
      ),
    );
  }
  update(quantity) {
    this.querySelectorAll('.quantity-gift').forEach((gift) => {
      gift.dataset.unlocked = String(quantity >= Number(gift.dataset.quantity || 1));
    });
  }
  getItems() {
    return Array.from(this.querySelectorAll('.quantity-gift[data-unlocked="true"]')).map((gift) => ({
      id: gift.dataset.product,
      quantity: 1,
    }));
  }
}
ihDefine('quantity-gifts', QuantityGifts);
class BundleDeals extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.addEventListener('change', (event) => {
      const selectors = event.target.closest('.bundle-deals__variant-selects-js');
      if (selectors) {
        const variant = ihSelectedVariant(selectors),
          row = selectors.closest('.bundle-deals__product-js') || selectors.closest('.upsell'),
          input = row?.querySelector('.bundle-deals__checkbox-js');
        if (variant && input) {
          input.dataset.id = variant.id;
          input.dataset.price = Math.max(
            0,
            variant.price * Number(selectors.dataset.percentageLeft || 1) -
              Number(selectors.dataset.fixedDiscount || 0),
          );
          input.dataset.comparePrice = variant.compare_at_price || variant.price;
          input.disabled = !variant.available;
          const image = row.querySelector('.bundle-deals__media-item-img-js');
          if (image && variant.featured_image?.src) image.src = variant.featured_image.src;
        }
      }
      this.update();
    });
    this.update();
  }
  getItems() {
    return ihCombineItems(
      Array.from(this.querySelectorAll('.bundle-deals__checkbox-js:checked:not(:disabled)')).map((input) => ({
        id: input.dataset.id,
        quantity: 1,
      })),
    );
  }
  update() {
    let price = 0,
      compare = 0;
    this.querySelectorAll('.bundle-deals__checkbox-js').forEach((input) => {
      input.dataset.checked = String(input.checked);
      if (input.checked) {
        price += Number(input.dataset.price || 0);
        compare += Number(input.dataset.comparePrice || input.dataset.price || 0);
      }
      const row = input.closest('.bundle-deals__product-js');
      row
        ?.querySelectorAll('.bundle-deals__price-js')
        .forEach((node) => (node.textContent = ihMoney(input.dataset.price, this.dataset.moneyFormat)));
      row
        ?.querySelectorAll('.bundle-deals__compare-price-js')
        .forEach(
          (node) =>
            (node.textContent =
              Number(input.dataset.comparePrice) > Number(input.dataset.price)
                ? ihMoney(input.dataset.comparePrice, this.dataset.moneyFormat)
                : ''),
        );
    });
    price = Math.max(
      0,
      price * Number(this.dataset.percentageLeft || 1) - Number(this.dataset.fixedDiscount || 0),
    );
    this.querySelectorAll('.bundle-deals__total-price-js').forEach(
      (node) => (node.textContent = ihMoney(price, this.dataset.moneyFormat)),
    );
    this.querySelectorAll('.bundle-deals__total-compare-price-js').forEach(
      (node) => (node.textContent = compare > price ? ihMoney(compare, this.dataset.moneyFormat) : ''),
    );
    const button = this.querySelector('button[type="submit"]');
    if (button) button.disabled = this.getItems().length === 0;
  }
}
ihDefine('bundle-deals', BundleDeals);
class MainBundleOffer extends BundleDeals {}
ihDefine('main-bundle-offer', MainBundleOffer);
class ProductInfoUpsell extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    this.addEventListener('click', (event) => {
      if (event.target.closest('a,select,input,label')) return;
      if (
        event.target.closest('.upsell-toggle-btn,.upsell--container-btn') ||
        this.classList.contains('upsell--container-btn')
      ) {
        event.preventDefault();
        this.dataset.selected = String(this.dataset.selected !== 'true');
        this.syncToggle();
      }
    });
    this.addEventListener('change', (event) => {
      if (event.target.matches('select')) {
        const variant = ihSelectedVariant(this);
        if (variant) {
          this.dataset.id = variant.id;
          this.dataset.price = Math.max(
            0,
            variant.price * Number(this.dataset.percentageLeft || 1) -
              Number(this.dataset.fixedDiscount || 0),
          );
          this.dataset.comparePrice = variant.compare_at_price || variant.price;
          this.querySelectorAll('.regular-price').forEach(
            (node) => (node.textContent = ihMoney(this.dataset.price, this.dataset.moneyFormat)),
          );
          this.querySelectorAll('.compare-price').forEach(
            (node) =>
              (node.textContent =
                Number(this.dataset.comparePrice) > Number(this.dataset.price)
                  ? ihMoney(this.dataset.comparePrice, this.dataset.moneyFormat)
                  : ''),
          );
        }
      }
      if (event.target.matches('input[type="checkbox"]')) {
        this.dataset.selected = String(event.target.checked);
        this.syncToggle();
      }
    });
    this.syncToggle();
  }
  syncToggle() {
    this.querySelectorAll('.upsell-toggle-btn').forEach((button) =>
      button.setAttribute('aria-pressed', this.dataset.selected || 'false'),
    );
    this.querySelectorAll('input[type="checkbox"]:not(.bundle-deals__checkbox-js)').forEach(
      (input) => (input.checked = this.dataset.selected === 'true'),
    );
  }
}
ihDefine('product-info-upsell', ProductInfoUpsell);
class CartDrawerUpsell extends ProductInfoUpsell {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', async (event) => {
      const button = event.target.closest('.upsell-toggle-btn,.upsell--container-btn');
      if (!button || this._pending) return;
      event.preventDefault();
      this._pending = true;
      try {
        const current = await ihJSON(ihRoot() + 'cart.js'),
          existing = current.items.find((item) => String(item.variant_id) === this.dataset.id);
        if (existing && this.dataset.toggle === 'true') {
          await ihChangeCart(existing.key, 0);
        } else {
          const state = await ihJSON(ihRoutes().cart_add_url + '.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: this.dataset.id, quantity: 1 }],
              sections: ihCartSections(),
              sections_url: location.pathname,
            }),
          });
          ihApplyCart(state);
          this.dataset.selected = 'true';
          this.syncToggle();
        }
      } catch (error) {
        const errorNode = this.querySelector('.product-form__error-message');
        if (errorNode) errorNode.textContent = error.message;
        else console.warn(error.message);
      } finally {
        this._pending = false;
      }
    });
  }
}
ihDefine('cart-drawer-upsell', CartDrawerUpsell);
class CustomProductField extends HTMLElement {
  connectedCallback() {
    this.addEventListener('input', () => {
      this.validate(false);
    });
    this.addEventListener('change', () => this.validate(false));
  }
  getValue() {
    return (
      this.querySelector('input[type="radio"]:checked')?.value ||
      this.querySelector('input:not([type="radio"]),textarea,select')?.value ||
      ''
    );
  }
  validate(show = true) {
    const inputs = Array.from(this.querySelectorAll('input,textarea,select')),
      required = this.dataset.required === 'true';
    const valid =
      (!required || this.getValue().trim() !== '') && inputs.every((input) => input.checkValidity());
    const error = this.querySelector('.input-error-msg');
    if (error) error.style.display = show && !valid ? 'block' : 'none';
    if (show && !valid) inputs[0]?.focus();
    return valid;
  }
}
ihDefine('custom-product-field', CustomProductField);
class DynamicDates extends HTMLElement {
  connectedCallback() {
    const days = (this.dataset.dayLabels || 'Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday').split(
        ',',
      ),
      months = (
        this.dataset.monthLabels ||
        'January,February,March,April,May,June,July,August,September,October,November,December'
      ).split(',');
    const format = (n) => {
      const date = new Date();
      date.setDate(date.getDate() + Number(n || 0));
      const values = {
        day: days[date.getDay()],
        date: date.getDate(),
        month: months[date.getMonth()],
        year: date.getFullYear(),
      };
      const pattern = this.dataset.dateFormat || 'day, month date';
      return pattern.replace(/\[?(day|date|month|year)\]?/g, (_, key) => values[key]);
    };
    this.querySelectorAll('[data-dynamic-date="true"]').forEach(
      (node) =>
        (node.innerHTML = (node.dataset.text || node.innerHTML)
          .replaceAll('[start_date]', format(node.dataset.minDays))
          .replaceAll('[end_date]', format(node.dataset.maxDays))),
    );
  }
}
ihDefine('dynamic-dates', DynamicDates);
class ComparisonSlider extends HTMLElement {
  connectedCallback() {
    const input = this.querySelector('input[type="range"]'),
      overlay = this.querySelector('.comparison-slider__overlay'),
      line = this.querySelector('.comparison-slider__line');
    const update = () => {
      const percent = Number(input.value);
      if (overlay) overlay.style.width = percent + '%';
      if (line) line.style.left = percent + '%';
      const image = overlay?.querySelector('img');
      if (image) image.style.width = this.getBoundingClientRect().width + 'px';
    };
    input?.addEventListener('input', update);
    this._resize = new ResizeObserver(update);
    this._resize.observe(this);
    if (input) update();
  }
  disconnectedCallback() {
    this._resize?.disconnect();
  }
}
ihDefine('comparison-slider', ComparisonSlider);
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const target =
      document.getElementById('ProductSubmitButton-' + this.dataset.section) ||
      document.querySelector('.main-product-form');
    if (this.dataset.afterScroll === 'true' && target) {
      this._observer = new IntersectionObserver(
        (entries) => {
          this.style.transform =
            entries[0].boundingClientRect.bottom < 0 ? 'translateY(0)' : 'translateY(100%)';
        },
        { threshold: 0 },
      );
      this._observer.observe(target);
    }
    this.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (this.dataset.scrollBtn === 'true') {
        event.preventDefault();
        let destination;
        try {
          destination = document.querySelector(this.dataset.scrollDestination);
        } catch {}
        (destination || target)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const form = document.getElementById('product-form-' + this.dataset.section);
      if (form && button.type !== 'submit') {
        event.preventDefault();
        form.requestSubmit();
      }
    });
  }
  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
ihDefine('sticky-atc', StickyAtc);
class SecondaryVariantSelect extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', () => {
      const primary = document.querySelector(`variant-selects[data-section="${this.dataset.section}"]`);
      if (!primary) return;
      const selects = Array.from(this.querySelectorAll('select'));
      let options;
      if (selects.length === 1 && selects[0].selectedOptions[0]?.dataset.options)
        options = selects[0].selectedOptions[0].dataset.options.split(',');
      else {
        const variant = primary.getVariantData()?.find((v) => String(v.id) === selects[0]?.value);
        options = variant?.options || selects.map((select) => select.value);
      }
      Array.from(primary.querySelectorAll('.product-form__input'))
        .filter((group) => group.querySelector('select,input[type="radio"]'))
        .forEach((group, index) => {
          const select = group.querySelector('select');
          if (select) select.value = options[index];
          else
            group
              .querySelectorAll('input')
              .forEach((input) => (input.checked = input.value === options[index]));
        });
      primary.onVariantChange();
    });
    this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event.data?.sectionId !== this.dataset.section) return;
      const variant = event.data.variant;
      this.querySelectorAll('select').forEach(
        (select, index) =>
          (select.value = select.classList.contains('sticky-atc__variant-select')
            ? variant.id
            : variant.options[index]),
      );
    });
  }
  disconnectedCallback() {
    this._unsub?.();
  }
}
ihDefine('secondary-variant-select', SecondaryVariantSelect);
class ModalV2 extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target.closest('.modal-v2__open-btn')) this.open(event.target);
      if (event.target.closest('.modal-v2__close-btn')) this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }
  open(opener) {
    this.opener = opener;
    this.dataset.open = 'true';
    document.body.classList.add('overflow-hidden');
    trapFocus(this.querySelector('.modal-v2__modal') || this);
  }
  close() {
    this.dataset.open = 'false';
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus(this.opener);
  }
}
ihDefine('modal-v2', ModalV2);
class PromoPopup extends HTMLElement {
  connectedCallback() {
    this.key =
      'ih-promo-' + location.host + '-' + (this.id || this.closest('.shopify-section')?.id || 'default');
    let shown = 0;
    try {
      shown = Number(localStorage.getItem(this.key) || 0);
    } catch {}
    const days = Number(this.dataset.delayDays || 1);
    this.querySelectorAll('.promp-popup__close-btn').forEach((button) =>
      button.addEventListener('click', () => this.close()),
    );
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
    if (this.dataset.testMode === 'true' || Date.now() - shown > days * 86400000)
      this._timeout = setTimeout(
        () => this.open(),
        Math.max(0, Number(this.dataset.delaySeconds || 0)) * 1000,
      );
  }
  open() {
    this.previous = document.activeElement;
    this.querySelector('.popup-overlay')?.classList.add('popup-overlay--active');
    this.querySelector('.popup-modal')?.classList.add('popup-modal--active');
    trapFocus(this.querySelector('.popup-modal'));
    if (this.dataset.displayTimer === 'true') {
      let remaining = Number(this.dataset.timerDuration || 5) * 60;
      const tick = () => {
        const minutes = this.querySelector('.popup-modal__timer__minutes'),
          seconds = this.querySelector('.popup-modal__timer__seconds');
        if (minutes) minutes.textContent = String(Math.floor(remaining / 60)).padStart(2, '0');
        if (seconds) seconds.textContent = String(remaining % 60).padStart(2, '0');
        remaining--;
        if (remaining < 0) clearInterval(this._interval);
      };
      tick();
      this._interval = setInterval(tick, 1000);
    }
  }
  close() {
    this.querySelector('.popup-overlay')?.classList.remove('popup-overlay--active');
    this.querySelector('.popup-modal')?.classList.remove('popup-modal--active');
    try {
      localStorage.setItem(this.key, String(Date.now()));
    } catch {}
    clearInterval(this._interval);
    removeTrapFocus(this.previous);
  }
  disconnectedCallback() {
    clearTimeout(this._timeout);
    clearInterval(this._interval);
  }
}
ihDefine('promo-popup', PromoPopup);
class SectionGroup extends HTMLElement {
  connectedCallback() {
    const move = () => {
      if (Shopify.designMode) return;
      for (const [selector, key] of [
        [this.dataset.sectionOneId, 'one'],
        [this.dataset.sectionTwoId, 'two'],
      ]) {
        if (!selector) continue;
        let section;
        try {
          section =
            document.querySelector(selector.startsWith('#') ? selector : '#shopify-section-' + selector) ||
            document.querySelector(selector);
        } catch {}
        const content = section?.querySelector('.content-for-grouping') || section;
        const destination = this.querySelector('.section-group__section-' + key + '-container');
        if (content && destination && !content.contains(this)) {
          destination.append(content);
          if (section !== content) section.hidden = true;
        }
      }
    };
    if (document.readyState === 'loading')
      document.addEventListener('DOMContentLoaded', move, { once: true });
    else move();
  }
}
ihDefine('section-group', SectionGroup);
class ClickableDiscount extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', async (event) => {
      event.preventDefault();
      if (this.dataset.loading === 'true') return;
      this.dataset.loading = 'true';
      try {
        const response = await fetch(
          ihRoot() + 'discount/' + encodeURIComponent(this.dataset.code) + '?redirect=/cart',
          { credentials: 'same-origin' },
        );
        if (!response.ok) throw new Error();
        this.dataset.applied = 'true';
        this.dataset.error = 'false';
      } catch {
        this.dataset.error = 'true';
      } finally {
        this.dataset.loading = 'false';
      }
    });
  }
}
ihDefine('clickable-discount', ClickableDiscount);

class InternalVideo extends HTMLElement {
  connectedCallback() {
    const video = this.querySelector('video');
    if (!video) return;
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', () => {
        if (button.className.includes('mute')) video.muted = !video.muted;
        else video.paused ? video.play().catch(() => {}) : video.pause();
      }),
    );
    if (video.autoplay) {
      this._observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
      this._observer.observe(video);
    }
  }
  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
ihDefine('internal-video', InternalVideo);
class CartDrawerGift extends ProductInfoUpsell {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(
      'submit',
      (event) => {
        if (this.dataset.selected !== 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true,
    );
  }
}
ihDefine('cart-drawer-gift', CartDrawerGift);
